/* CARILOHA: White Glove guided-selection web component — ZIP-gated service picker that adds a
   real product variant to the cart. Ported from the legacy BigCommerce white-glove landing page
   (ai/white-glove/index.html): same LoadUp JSONP availability check and progressive step unlock,
   rewired to Shopify's variant/cart model instead of hardcoded SKUs and a cart.php redirect. */

import { Component } from '@theme/component';
/* CARILOHA: CartAddEvent (@theme/events) was retired upstream in favor of CartLinesUpdateEvent
   (@shopify/events), which is also what cart-icon.js now listens for to update the cart bubble
   and open the drawer — dispatch it with a deferred promise before the fetch, matching the
   pattern in assets/product-form.js. */
import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} WhiteGloveVariant
 * @property {number} id
 * @property {string} title
 * @property {string} sku
 * @property {number} price
 * @property {boolean} available
 */

/**
 * @typedef {Object} WhiteGloveConfig
 * @property {string} productTitle
 * @property {string} moneyFormat
 * @property {string} currency
 * @property {string} zipApiUrl
 * @property {WhiteGloveVariant[]} variants
 * @property {Record<string, string>} descriptions - SKU -> merchant-authored blurb
 */

/**
 * @typedef {Object} WhiteGloveRefs
 * @property {HTMLInputElement} zipInput
 * @property {HTMLButtonElement} zipCheckButton
 * @property {HTMLElement} zipStatus
 * @property {HTMLElement} step1
 * @property {HTMLElement} step2
 * @property {HTMLElement} serviceCards
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} cartStatus
 * @property {HTMLElement} liveRegion
 */

/** @extends {Component<WhiteGloveRefs>} */
export class WhiteGloveComponent extends Component {
  /** @type {WhiteGloveConfig|null} */
  #config = null;

  /** @type {number|null} */
  #selectedVariantId = null;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  #init() {
    const configEl = document.getElementById(this.dataset.configId ?? '');
    if (!configEl) {
      console.error('[white-glove] Config script element not found:', this.dataset.configId);
      return;
    }
    try {
      this.#config = JSON.parse(configEl.textContent ?? '');
    } catch (e) {
      console.error('[white-glove] Failed to parse config JSON:', e);
      return;
    }

    this.#renderServiceCards();
  }

  /** Called by on:input="handleZipInput" */
  handleZipInput(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    input.value = input.value.replace(/[^0-9]/g, '');
  }

  // ─── Step 2: service cards ─────────────────────────────────────────────────

  #renderServiceCards() {
    const root = this.refs?.serviceCards;
    if (!root || !this.#config) return;

    root.innerHTML = this.#config.variants
      .map((variant) => {
        const description = this.#config?.descriptions[variant.sku] ?? '';
        const price = formatMoney(variant.price, this.#config?.moneyFormat ?? '${{amount}}', this.#config?.currency ?? 'USD');
        return `
          <button
            type="button"
            class="wg-service-card${!variant.available ? ' wg-service-card--unavailable' : ''}"
            role="radio"
            aria-checked="false"
            aria-label="Select ${this.#esc(variant.title)} for ${price}${description ? '. ' + this.#esc(description) : ''}"
            data-variant-id="${variant.id}"
            on:click="white-glove-component/handleServiceSelect?variantId=${variant.id}"
            ${!variant.available ? 'disabled aria-disabled="true"' : ''}
          >
            <h4 class="wg-service-card__title">${this.#esc(variant.title)}</h4>
            <div class="wg-service-card__price">${price}</div>
            ${description ? `<p class="wg-service-card__body">${this.#esc(description)}</p>` : ''}
            ${!variant.available ? '<p class="wg-service-card__body">Currently unavailable</p>' : ''}
          </button>
        `;
      })
      .join('');
  }

  /** Service card clicked. Called by on:click="handleServiceSelect?variantId=123" */
  handleServiceSelect(data) {
    if (this.refs.step2?.getAttribute('aria-disabled') === 'true') return;

    const variantId = Number(data?.variantId);
    if (!variantId) return;

    this.#selectedVariantId = variantId;

    for (const card of this.refs.serviceCards?.querySelectorAll('.wg-service-card') ?? []) {
      const isSelected = Number(card.getAttribute('data-variant-id')) === variantId;
      card.classList.toggle('wg-service-card--selected', isSelected);
      card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    }

    if (this.refs.addToCartButton) {
      this.refs.addToCartButton.disabled = false;
      setTimeout(() => {
        this.refs.addToCartButton?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }

  // ─── Step 1: ZIP availability check ────────────────────────────────────────

  /** Called by on:click="handleCheckZip" */
  handleCheckZip() {
    const zip = this.refs.zipInput?.value ?? '';
    if (!/^\d{5}$/.test(zip)) {
      this.#showZipMessage('Please enter a valid 5-digit ZIP code.', 'error');
      return;
    }

    const button = this.refs.zipCheckButton;
    button?.classList.add('wg-btn--loading');
    if (button) button.disabled = true;
    if (this.refs.zipStatus) this.refs.zipStatus.className = 'wg-status-message';

    const callbackName = `whiteGloveZipCallback_${Date.now()}`;
    const cleanup = () => {
      delete /** @type {any} */ (window)[callbackName];
      document.getElementById(callbackName)?.remove();
      button?.classList.remove('wg-btn--loading');
      if (button) button.disabled = false;
    };

    /** @type {any} */ (window)[callbackName] = (/** @type {{ available?: boolean }} */ responseData) => {
      if (responseData && responseData.available === true) {
        this.#showZipMessage('White Glove Service is Available', 'success', true);
        this.#unlockStep2();
      } else {
        this.#showZipMessage(
          'While we offer this service to more than 80% of U.S. zip codes, it is not offered to your area at this time. We are sorry for the inconvenience.',
          'error'
        );
        this.#lockStep2();
      }
      cleanup();
    };

    const script = document.createElement('script');
    script.id = callbackName;
    const apiUrl = this.#config?.zipApiUrl || 'https://order.goloadup.com/partner/api/service_availability';
    script.src = `${apiUrl}?zip=${encodeURIComponent(zip)}&callback=${callbackName}`;

    const timeout = setTimeout(() => {
      if (/** @type {any} */ (window)[callbackName]) {
        this.#showZipMessage('We couldn’t confirm service availability right now. Please try again.', 'error');
        cleanup();
      }
    }, 8000);

    script.onerror = () => {
      clearTimeout(timeout);
      this.#showZipMessage('Connection error. Please try again.', 'error');
      cleanup();
    };

    document.body.appendChild(script);
  }

  /**
   * @param {string} message
   * @param {'success'|'error'} type
   * @param {boolean} [isHtml]
   */
  #showZipMessage(message, type, isHtml = false) {
    const status = this.refs.zipStatus;
    if (!status) return;
    if (isHtml) {
      status.innerHTML = `${this.#checkIcon()}<span class="wg-status-message__text">${message}</span>`;
    } else {
      status.textContent = message;
    }
    status.className = `wg-status-message wg-status-message--${type}`;
  }

  #unlockStep2() {
    const { step1, step2 } = this.refs;
    if (!step2) return;
    step2.classList.remove('wg-flow-step--locked');
    step2.setAttribute('aria-disabled', 'false');
    step2.classList.add('wg-flow-step--active');
    step1?.classList.remove('wg-flow-step--active');

    setTimeout(() => {
      step2.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 1000);
  }

  #lockStep2() {
    const { step2, addToCartButton } = this.refs;
    step2?.classList.add('wg-flow-step--locked');
    step2?.setAttribute('aria-disabled', 'true');
    step2?.classList.remove('wg-flow-step--active');
    if (addToCartButton) addToCartButton.disabled = true;
  }

  // ─── Add to cart ─────────────────────────────────────────────────────────────

  /** Called by on:click="handleAddToCart" */
  handleAddToCart() {
    if (!this.#selectedVariantId || this.refs.addToCartButton?.disabled) return;
    this.#addToCart();
  }

  async #addToCart() {
    const button = this.refs.addToCartButton;
    button?.classList.add('wg-btn--loading');
    if (button) button.disabled = true;
    if (this.refs.cartStatus) this.refs.cartStatus.textContent = '';

    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    }

    const deferredAddPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: this.#selectedVariantId, quantity: 1 }],
        promise: deferredAddPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: this.#selectedVariantId, quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });

      const responseData = await response.json();

      /* CARILOHA: /cart/add.js's response has no top-level `currency` field (only
         full-cart endpoints like /cart.js and /cart/update.js do), but
         createCartFromAjaxResponse requires one to format line item prices —
         fetch the full cart afterward, matching the pattern in product-form.js's
         own #refreshCart(). */
      const currentCart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => response.json());

      if (responseData.status) {
        deferredAddPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(currentCart),
          detail: {
            didError: true,
            source: 'white-glove-component',
            itemCount: 1,
          },
        });
        this.#showCartMessage(responseData.message || 'Could not add this service to your cart.', 'error');
        return;
      }

      deferredAddPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(currentCart),
        detail: {
          source: 'white-glove-component',
          itemCount: 1,
          sections: responseData.sections,
          didError: false,
        },
      });

      this.#showCartMessage('Added to your cart.', 'success');

      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent = 'White Glove service added to cart.';
        setTimeout(() => {
          if (this.refs.liveRegion) this.refs.liveRegion.textContent = '';
        }, 5000);
      }
    } catch (error) {
      console.error('[white-glove] Add to cart failed:', error);
      deferredAddPromise.reject(error);
      this.#showCartMessage('Network error. Please try again.', 'error');
    } finally {
      button?.classList.remove('wg-btn--loading');
      if (button) button.disabled = false;
    }
  }

  /**
   * @param {string} message
   * @param {'success'|'error'} type
   */
  #showCartMessage(message, type) {
    const status = this.refs.cartStatus;
    if (!status) return;
    status.textContent = message;
    status.className = `wg-status-message wg-status-message--${type}`;
  }

  // ─── Utilities ───────────────────────────────────────────────────────────────

  /**
   * @param {string} str
   * @returns {string}
   */
  #esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  #checkIcon() {
    return `<svg class="wg-status-message__icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18z" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M8 12.3l2.6 2.6L16 9.4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }
}

if (!customElements.get('white-glove-component')) {
  customElements.define('white-glove-component', WhiteGloveComponent);
}
